import {Chip, Tooltip} from "@mui/material";
import CopyableTextHoverOnly from "../components/ui/CopyableTextHoverOnly";
import LinkedRolesChips from "../components/ui/LinkedRolesChips";
import {toLocalDate, getTokenIssuedAt} from "../utils/formatData";

const rolesColumns = [
  {
    field: "name",
    headerName: "Name",
    width: 150,
    editable: false
  },
  {
    field: "product_name",
    headerName: "Product",
    width: 150,
    editable: false
  },
  {
    field: "description",
    headerName: "Description",
    width: 150,
    editable: false
  }
];

const userColumns = [
  {
    field: "name",
    headerName: "Nombre",
    width: 150,
    editable: false
  },
  {
    field: "surname",
    headerName: "Apellidos",
    width: 150,
    editable: false
  },
  {
    field: "email",
    headerName: "Email",
    width: 200,
    editable: false,
    renderCell: (params) => <CopyableTextHoverOnly value={params.value} label="Email" />
  },
  {
    field: "ts_email",
    headerName: "TS Email",
    width: 200,
    editable: false,
    renderCell: (params) => <CopyableTextHoverOnly value={params.value} label="TS Email" />
  },
  {
    field: "created_at",
    headerName: "Fecha de creación",
    width: 180,
    editable: false,
    renderCell: (params) => toLocalDate(params.value)
  }
];

const clientColumns = [
  {
    field: "name",
    headerName: "Nombre",
    width: 150,
    editable: false,
    renderCell: (params) => <div style={{display: "flex", alignItems: "center", height: "100%"}}>{params.value}</div>
  },
  {
    field: "allowed_grants",
    headerName: "Allowed grants",
    width: 400,
    editable: false,
    sortable: false,
    renderCell: (params) => {
      const grants = params.value;
      if (!grants || grants.length === 0) return "-";
      if (!Array.isArray(grants)) return grants;

      const chipColors = [
        {name: "password", color: "#1976d2"},
        {name: "authorization_code", color: "#9c27b0"},
        {name: "client_credentials", color: "#f57c00"},
        {name: "refresh_token", color: "#d32f2f"},
        {name: "token_exchange", color: "#388e3c"}
      ];

      const getColorForGrant = (grantName) => {
        const grantColor = chipColors.find((c) => c.name === grantName);
        return grantColor ? grantColor.color : "#757575";
      };

      return (
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: "6px",
            width: "100%",
            alignItems: "center",
            padding: "12px 0",
            minHeight: "52px"
          }}
        >
          {grants.map((grant, index) => (
            <Chip
              key={index}
              label={grant}
              size="small"
              style={{borderColor: getColorForGrant(grant), color: getColorForGrant(grant)}}
              variant="outlined"
            />
          ))}
        </div>
      );
    }
  },
  {
    field: "redirect_uris",
    headerName: "Redirect URIs",
    width: 120,
    editable: false,
    sortable: false,
    renderCell: (params) => <div style={{display: "flex", alignItems: "center", height: "100%"}}>{params.value}</div>
  },
  {
    field: "created_at",
    headerName: "Fecha de creación",
    width: 180,
    editable: false,
    renderCell: (params) => (
      <div style={{display: "flex", alignItems: "center", height: "100%"}}>{toLocalDate(params.value)}</div>
    )
  }
];

const m2mTokenColumns = [
  {
    field: "active",
    headerName: "Activo",
    flex: 0.5,
    minWidth: 90,
    editable: false,
    type: "boolean"
  },
  {
    field: "token",
    headerName: "Token",
    flex: 2.5,
    minWidth: 260,
    editable: false,
    renderCell: (params) => <CopyableTextHoverOnly value={params.value} label="Token" />
  },
  {
    field: "domain",
    headerName: "Domain",
    flex: 1,
    minWidth: 140,
    editable: false
  },
  {
    field: "payload",
    headerName: "Actions",
    flex: 1.5,
    minWidth: 180,
    editable: false,
    sortable: false,
    renderCell: (params) => {
      return params.value ? JSON.stringify(params.value.actions) : "";
    }
  },
  {
    field: "created_at",
    headerName: "Fecha creación",
    flex: 1,
    minWidth: 150,
    editable: false,
    renderCell: (params) => {
      return toLocalDate(params.value);
    }
  },
  {
    field: "expiration",
    headerName: "Fecha expiración",
    flex: 1,
    minWidth: 150,
    editable: false,
    sortable: false,
    renderCell: (params) => {
      const payload = params.row?.payload;
      if (payload && payload.exp) {
        return toLocalDate(new Date(payload.exp * 1000).toISOString());
      }
      return "";
    }
  }
];

const tokenColumns = [
  {
    field: "token",
    headerName: "Token",
    width: 200,
    editable: false,
    renderCell: (params) => <CopyableTextHoverOnly value={params.value} label="Token" />
  },
  {
    field: "type",
    headerName: "Tipo",
    width: 200,
    editable: false
  },
  {
    field: "created_at",
    headerName: "Fecha de emisión",
    width: 180,
    editable: false,
    sortable: true,
    valueGetter: (value, row) => getTokenIssuedAt(row).issuedAt || null,
    renderCell: (params) => {
      const {issuedAt, estimated} = getTokenIssuedAt(params.row);
      if (!issuedAt) {
        return (
          <Tooltip title="No se puede calcular: la expiración de este token no corresponde a la duración configurada para su tipo">
            <span>-</span>
          </Tooltip>
        );
      }
      if (!estimated) return toLocalDate(issuedAt);

      return (
        <Tooltip title="Fecha estimada a partir de la expiración y la duración del token">
          <span>≈ {toLocalDate(issuedAt)}</span>
        </Tooltip>
      );
    }
  },
  {
    field: "expires_at",
    headerName: "Fecha expiración",
    width: 180,
    editable: false,
    renderCell: (params) => {
      return toLocalDate(params.value);
    }
  },
  {
    field: "email",
    headerName: "Usuario",
    width: 180,
    editable: false
  }
];

const scopesColumns = [
  {
    field: "product_name",
    headerName: "Producto",
    width: 150,
    editable: false,
    renderCell: (params) => params.value || "-"
  },
  {
    field: "resource",
    headerName: "Recurso",
    width: 120,
    editable: false
  },
  {
    field: "subresource",
    headerName: "Subrecurso",
    width: 120,
    editable: false,
    renderCell: (params) => params.value || "-"
  },
  {
    field: "ownership",
    headerName: "Alcance",
    width: 100,
    editable: false,
    renderCell: (params) => {
      const map = {global: "Global", tenant: "Tenant", self: "Self"};
      return map[params.value] || params.value;
    }
  },
  {
    field: "action",
    headerName: "Acción",
    width: 100,
    editable: false
  },

  {
    field: "description",
    headerName: "Descripción",
    width: 250,
    editable: false
  }
];

const joinInternalCodes = (codes) => (Array.isArray(codes) ? codes.join(", ") : codes || "");

// `linked_users.by_role` se itera tal cual lo devuelve el backend: no hay nombres de rol fijos.
const getLinkedRoles = (row) => row?.linked_users?.by_role ?? [];
const getLinkedUsersTotal = (row) => row?.linked_users?.total ?? 0;
const joinLinkedRoles = (row) =>
  getLinkedRoles(row)
    .map((role) => `${role.name}: ${role.count}`)
    .join(", ") || "Sin usuarios";

const tenantsColumns = [
  {
    field: "name",
    headerName: "Nombre",
    width: 150,
    editable: false
  },
  {
    field: "nif",
    headerName: "NIF",
    width: 150,
    editable: false
  },
  {
    field: "status",
    headerName: "Estado",
    width: 150,
    editable: false,
    renderCell: (params) => (params.value === "active" ? "Activo" : "Inactivo")
  },
  {
    field: "internal_codes",
    headerName: "Código interno",
    width: 200,
    editable: false,
    sortable: false,
    renderCell: (params) => joinInternalCodes(params.value)
  },
  {
    field: "linked_users",
    headerName: "Usuarios",
    flex: 1,
    minWidth: 90,
    editable: false,
    valueGetter: (value) => value?.total ?? 0
  },
  {
    field: "linked_roles",
    headerName: "Roles vinculados",
    flex: 2.5,
    minWidth: 260,
    editable: false,
    sortable: false,
    filterable: false,
    renderCell: (params) => {
      const roles = getLinkedRoles(params.row);
      if (roles.length === 0) return "Sin usuarios";

      return <LinkedRolesChips roles={roles} />;
    }
  },
  {
    field: "created_at",
    headerName: "Fecha de creación",
    width: 180,
    editable: false,
    renderCell: (params) => {
      return toLocalDate(params.value);
    }
  }
];

const tenantProductsColumns = [
  {
    field: "product_name",
    headerName: "Producto",
    width: 250,
    editable: false
  },
  {
    field: "internal_code",
    headerName: "Código Interno",
    width: 250,
    editable: false
  }
];

const tenantUserRolesColumns = [
  {
    field: "tenant_name",
    headerName: "Tenant",
    width: 200,
    editable: false
  },
  {
    field: "user_email",
    headerName: "Usuario",
    width: 200,
    editable: false
  },
  {
    field: "product_name",
    headerName: "Producto",
    width: 200,
    editable: false
  },
  {
    field: "role_name",
    headerName: "Rol",
    width: 200,
    editable: false
  },
  {
    field: "assigned_at",
    headerName: "Asignado",
    width: 180,
    editable: false,
    renderCell: (params) => {
      return toLocalDate(params.value);
    }
  },
  {
    field: "assigner_email",
    headerName: "Asignado por",
    width: 250,
    editable: false
  }
];

const productsColumns = [
  {
    field: "code",
    headerName: "Código",
    width: 150,
    editable: false
  },
  {
    field: "name",
    headerName: "Nombre",
    width: 150,
    editable: false
  },
  {
    field: "description",
    headerName: "Descripción",
    width: 250,
    editable: false
  },
  {
    field: "sync_type",
    headerName: "Tipo de sincronización",
    width: 180,
    editable: false,
    renderCell: (params) => {
      const map = {manual: "Manual", sync: "Sync", auto: "Auto"};
      return map[params.value] || params.value || "-";
    }
  }
];

const ticketColumns = [
  {
    field: "id",
    headerName: "Identificador",
    minWidth: 300,
    flex: 1,
    editable: false
  },
  {
    field: "ticket_type_label",
    headerName: "Tipo",
    minWidth: 120,
    flex: 0.6,
    editable: false
  },
  {
    field: "email",
    headerName: "Email",
    minWidth: 220,
    flex: 1,
    editable: false,
    renderCell: (params) => <CopyableTextHoverOnly value={params.value} label="Email" />
  },
  {
    // `decorateTicket` ya resuelve el texto (nombre del producto o "Sin producto").
    field: "product_display",
    headerName: "Producto",
    minWidth: 200,
    flex: 1,
    editable: false
  },
  {
    field: "contract_display",
    headerName: "Contrato",
    minWidth: 180,
    flex: 1,
    editable: false
  },
  {
    field: "expires_at",
    headerName: "Expira (aprox.)",
    minWidth: 170,
    flex: 0.8,
    editable: false,
    renderCell: (params) => (params.value ? toLocalDate(params.value) : "-")
  },
  {
    field: "status",
    headerName: "Estado",
    minWidth: 160,
    flex: 0.7,
    sortable: false,
    editable: false,
    // El backend no expone estado: si el ticket sale del SCAN de Redis, está vigente.
    renderCell: (params) =>
      params.row?.near_expiry ? (
        <Chip label="Próximo a expirar" size="small" color="warning" variant="outlined" />
      ) : (
        <Chip label="En curso" size="small" color="success" variant="outlined" />
      )
  }
];

const tenantsExportColumns = tenantsColumns.map((column) => {
  if (column.field === "status") {
    return {...column, format: (value) => (value === "active" ? "Activo" : "Inactivo")};
  }

  if (column.field === "internal_codes") {
    return {...column, format: joinInternalCodes};
  }

  // La exportación lee las filas crudas: el total sale de `linked_users`, no del valueGetter.
  if (column.field === "linked_users") {
    return {...column, format: (_value, row) => getLinkedUsersTotal(row)};
  }

  if (column.field === "linked_roles") {
    return {...column, format: (_value, row) => joinLinkedRoles(row)};
  }

  return column;
});

export {
  rolesColumns,
  userColumns,
  tenantsExportColumns,
  clientColumns,
  tokenColumns,
  scopesColumns,
  m2mTokenColumns,
  tenantsColumns,
  tenantProductsColumns,
  tenantUserRolesColumns,
  productsColumns,
  ticketColumns
};
